import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { parsePhoneNumber, isValidPhoneNumber, AsYouType } from 'libphonenumber-js';
import CookiePreferencesSettings from '../components/CookiePreferencesSettings';
import {
  Box,
  Container,
  Button,
  TextField,
  Typography,
  Paper,
  Divider,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  CardActions,
  Snackbar,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Radio,
  RadioGroup,
  FormLabel
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import {
  Save as SaveIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Security as SecurityIcon,
  Key as KeyIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Phone as PhoneIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Google as GoogleIcon,
  Microsoft as MicrosoftIcon,
  PhoneAndroid as PhoneAndroidIcon,
  Sms as SmsIcon,
  ContentCopy as ContentCopyIcon
} from '@mui/icons-material';
import { LinearProgress } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { ledeSx } from '../theme/styleHelpers';
import { organizationService, authService, userService } from '../services';
import { UserDeletionValidation, ContentAffirmationStatus } from '../services/user/user.service';
import UserDeletionValidationDialog from '../components/common/dialogs/UserDeletionValidationDialog';

// Password validation rules (matching Register.tsx) - defined outside component
const validateLowercase = (password: string): boolean => /[a-z]/.test(password);
const validateUppercase = (password: string): boolean => /[A-Z]/.test(password);
const validateNumber = (password: string): boolean => /\d/.test(password);
const validateSpecialChar = (password: string): boolean => /[@$!%*?&~]/.test(password);
const validateLength = (password: string): boolean => password.length >= 8;

const Profile: React.FC = () => {
  const { user, refreshUser, updateUser, updateUserProfile, logout } = useAuth();
  const { setThemeMode } = useTheme();

  // Note: name, bio, phoneNumber now come directly from user object
  // No local state needed - updateUserProfile in AuthContext handles autosave
  const [email, setEmail] = useState(user?.email || '');

  // Preferences states
  const [language, setLanguage] = useState('en');
  const [timezone, setTimezone] = useState('UTC');
  const [theme, setTheme] = useState<'light' | 'dark' | 'auto'>('auto');
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [smsNotifications, setSmsNotifications] = useState(false);

  // Password states
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Loading and message states
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', severity: 'info' });
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [verificationEmailMessage, setVerificationEmailMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  // 2FA states
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(user?.twoFactorEnabled || false);
  const [setupData, setSetupData] = useState({ secret: '', qrCodeUrl: '', backupCodes: [] as string[] });
  const [verificationCode, setVerificationCode] = useState('');
  const [twoFactorMethod, setTwoFactorMethod] = useState<'totp' | 'otp'>('totp');
  const [otpChannel, setOtpChannel] = useState<'email' | 'sms'>('email');
  const [otpSent, setOtpSent] = useState(false);
  const [otpExpiresAt, setOtpExpiresAt] = useState<string | null>(null);

  // Dual authentication states
  const [authMethods, setAuthMethods] = useState<{
    hasPassword: boolean;
    oauthProviders: string[];
    totalMethods: number;
  }>({ hasPassword: false, oauthProviders: [], totalMethods: 0 });
  const [setPasswordDialogOpen, setSetPasswordDialogOpen] = useState(false);
  const [initialPassword, setInitialPassword] = useState('');
  const [confirmInitialPassword, setConfirmInitialPassword] = useState('');
  const [initialPasswordLoading, setInitialPasswordLoading] = useState(false);

  // Content affirmation states
  const [affirmationStatus, setAffirmationStatus] = useState<ContentAffirmationStatus | null>(null);
  const [affirmationLoading, setAffirmationLoading] = useState(false);

  // Dialog states
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  const [validationResult, setValidationResult] = useState<UserDeletionValidation | null>(null);
  const [validationLoading, setValidationLoading] = useState(false);
  const [showTwoFactorDialog, setShowTwoFactorDialog] = useState(false);
  const [showBackupCodesDialog, setShowBackupCodesDialog] = useState(false);
  const [showTotpSetupDialog, setShowTotpSetupDialog] = useState(false);
  const [showDisableTwoFactorDialog, setShowDisableTwoFactorDialog] = useState(false);
  const [showDisableTotpDialog, setShowDisableTotpDialog] = useState(false);
  const [backupCodesCopied, setBackupCodesCopied] = useState(false);

  // Handle phone number change with auto-formatting
  const handlePhoneChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!user) return;

    let input = e.target.value;

    // Allow empty string
    if (input === '') {
      // Normalize to E.164 format before saving
      updateUserProfile({ phoneNumber: '' });
      return;
    }

    // Ensure the input starts with '+' if it doesn't already
    // This is required for international phone number formatting
    if (!input.startsWith('+')) {
      input = '+' + input;
    }

    // Use AsYouType formatter for real-time formatting
    const formatter = new AsYouType();
    const formatted = formatter.input(input);

    // Only update if the digits actually changed
    const prevDigits = (user.phoneNumber || '').replace(/\D/g, '');
    const newDigits = formatted.replace(/\D/g, '');

    if (prevDigits !== newDigits) {
      // Normalize to E.164 format for SMS compatibility
      try {
        if (isValidPhoneNumber(formatted)) {
          const parsed = parsePhoneNumber(formatted);
          updateUserProfile({ phoneNumber: parsed.number }); // E.164: +15551234567
        } else {
          // Invalid format, save formatted version anyway (will show validation error)
          updateUserProfile({ phoneNumber: formatted });
        }
      } catch (error) {
        // Parsing failed, save as-is
        updateUserProfile({ phoneNumber: formatted });
      }
    }
  }, [user, updateUserProfile]);

  // Format phone number for display (E.164 -> International format)
  const formattedPhoneNumber = useMemo(() => {
    const phoneNumber = user?.phoneNumber || '';

    if (!phoneNumber || phoneNumber.trim() === '') {
      return '';
    }

    try {
      if (isValidPhoneNumber(phoneNumber)) {
        const parsed = parsePhoneNumber(phoneNumber);
        return parsed.formatInternational(); // "+1 555 123 4567"
      }
    } catch (error) {
      // If parsing fails, return as-is
    }

    return phoneNumber;
  }, [user?.phoneNumber]);

  // Memoized phone validation - now uses user.phoneNumber directly
  const phoneValidation = useMemo(() => {
    const phoneNumber = user?.phoneNumber || '';

    if (!phoneNumber || phoneNumber.trim() === '') {
      return { isValid: true, error: '' }; // Empty is valid
    }

    try {
      // Check if it's a valid phone number
      if (isValidPhoneNumber(phoneNumber)) {
        const parsed = parsePhoneNumber(phoneNumber);
        return {
          isValid: true,
          error: '',
          formatted: parsed.formatInternational(),
          country: parsed.country
        };
      } else {
        return {
          isValid: false,
          error: 'Invalid phone number format. Include country code (e.g., +1 for US/Canada).'
        };
      }
    } catch (error) {
      return {
        isValid: false,
        error: 'Invalid phone number format. Include country code (e.g., +1 for US/Canada).'
      };
    }
  }, [user?.phoneNumber]);

  // Memoized validation for new password (change password section)
  const isPasswordValid = useMemo(() =>
    validateLowercase(newPassword) &&
    validateUppercase(newPassword) &&
    validateNumber(newPassword) &&
    validateSpecialChar(newPassword) &&
    validateLength(newPassword),
    [newPassword]
  );

  const passwordStrength = useMemo(() =>
    [
      validateLength(newPassword),
      validateLowercase(newPassword),
      validateUppercase(newPassword),
      validateNumber(newPassword),
      validateSpecialChar(newPassword)
    ].filter(Boolean).length,
    [newPassword]
  );

  const getPasswordStrengthColor = useCallback(() => {
    if (passwordStrength <= 2) return 'error';
    if (passwordStrength <= 4) return 'warning';
    return 'success';
  }, [passwordStrength]);

  const passwordRequirements = useMemo(() => [
    { label: 'At least 8 characters', valid: validateLength(newPassword) },
    { label: 'One lowercase letter', valid: validateLowercase(newPassword) },
    { label: 'One uppercase letter', valid: validateUppercase(newPassword) },
    { label: 'One number', valid: validateNumber(newPassword) },
    { label: 'One special character (@$!%*?&~)', valid: validateSpecialChar(newPassword) },
  ], [newPassword]);

  // Validation for initial password (set password dialog)
  const isInitialPasswordValid = useMemo(() =>
    validateLowercase(initialPassword) &&
    validateUppercase(initialPassword) &&
    validateNumber(initialPassword) &&
    validateSpecialChar(initialPassword) &&
    validateLength(initialPassword),
    [initialPassword]
  );

  const initialPasswordStrength = useMemo(() =>
    [
      validateLength(initialPassword),
      validateLowercase(initialPassword),
      validateUppercase(initialPassword),
      validateNumber(initialPassword),
      validateSpecialChar(initialPassword)
    ].filter(Boolean).length,
    [initialPassword]
  );

  const getInitialPasswordStrengthColor = useCallback(() => {
    if (initialPasswordStrength <= 2) return 'error';
    if (initialPasswordStrength <= 4) return 'warning';
    return 'success';
  }, [initialPasswordStrength]);

  const initialPasswordRequirements = useMemo(() => [
    { label: 'At least 8 characters', valid: validateLength(initialPassword) },
    { label: 'One lowercase letter', valid: validateLowercase(initialPassword) },
    { label: 'One uppercase letter', valid: validateUppercase(initialPassword) },
    { label: 'One number', valid: validateNumber(initialPassword) },
    { label: 'One special character (@$!%*?&~)', valid: validateSpecialChar(initialPassword) },
  ], [initialPassword]);
  
  // Update form when user data changes (but only specific fields that matter)
  // Use refs to track previous values and avoid unnecessary updates
  const prevUserIdRef = useRef<string | null>(null);
  const prevPreferencesRef = useRef<any>(null);

  useEffect(() => {
    if (!user) return;

    // Only update email and 2FA settings if this is a new user (e.g., after login)
    if (prevUserIdRef.current !== user.id) {
      prevUserIdRef.current = user.id;
      setEmail(user.email || '');
      setTwoFactorEnabled(user.twoFactorEnabled);

      // Set default 2FA method based on what user has available
      const methods = user.verified_2fa_methods || [];
      if (methods.includes('totp')) {
        setTwoFactorMethod('totp');
      } else if (methods.includes('email') || (user.twoFactorEnabled && user.isEmailVerified)) {
        setTwoFactorMethod('otp');
        setOtpChannel('email');
      } else if (methods.includes('sms')) {
        setTwoFactorMethod('otp');
        setOtpChannel('sms');
      }
    }

    // Only update preferences if they actually changed (not just user object ref)
    const currentPrefs = JSON.stringify(user.preferences || {});
    if (prevPreferencesRef.current !== currentPrefs) {
      prevPreferencesRef.current = currentPrefs;
      const preferences = user.preferences;
      if (preferences) {
        setLanguage(preferences.language || 'en');
        setTimezone(preferences.timezone || 'UTC');
        setTheme(preferences.theme || 'auto');
        // CRITICAL FIX: API returns snake_case (email_notifications, sms_notifications)
        if (preferences.email_notifications !== undefined) {
          setEmailNotifications(preferences.email_notifications);
        }
        if (preferences.sms_notifications !== undefined) {
          setSmsNotifications(preferences.sms_notifications);
        }
      }
    }

    // Note: name, bio, phoneNumber come directly from user object (no local state)
  }, [user?.id, user?.preferences, user?.email, user?.twoFactorEnabled, user?.verified_2fa_methods, user?.isEmailVerified]);

  // Additional effect to ensure preferences stay in sync with user object
  // This handles edge cases where the JSON comparison might not trigger
  useEffect(() => {
    if (user?.preferences) {
      const prefs = user.preferences;
      setLanguage(prefs.language || 'en');
      setTimezone(prefs.timezone || 'UTC');
      setTheme(prefs.theme || 'auto');
      // CRITICAL FIX: API returns snake_case (email_notifications, sms_notifications)
      if (prefs.email_notifications !== undefined) {
        setEmailNotifications(prefs.email_notifications);
      }
      if (prefs.sms_notifications !== undefined) {
        setSmsNotifications(prefs.sms_notifications);
      }
    }
  }, [
    user?.preferences?.language,
    user?.preferences?.timezone,
    user?.preferences?.theme,
    user?.preferences?.email_notifications,
    user?.preferences?.sms_notifications
  ]);

  // Load authentication methods on mount ONLY (not on every user change)
  useEffect(() => {
    const loadAuthMethods = async () => {
      try {
        const methods = await authService.getAuthMethods();
        setAuthMethods(methods);
      } catch (error) {
        console.error('Failed to load authentication methods:', error);
      }
    };

    if (user) {
      loadAuthMethods();
    }
  }, []); // Empty dependency array - only run on mount

  // Load content affirmation status on mount
  useEffect(() => {
    const loadAffirmationStatus = async () => {
      try {
        const status = await userService.getContentAffirmationStatus();
        setAffirmationStatus(status);
      } catch (error) {
        console.error('Failed to load content affirmation status:', error);
      }
    };

    if (user) {
      loadAffirmationStatus();
    }
  }, []); // Empty dependency array - only run on mount

  // Handle preference changes (immediate save via AuthContext)
  const handlePreferenceChange = useCallback((key: string, value: any) => {
    if (!user?.preferences) return;

    // Update local state first for immediate UI feedback
    switch(key) {
      case 'theme':
        setTheme(value);
        setThemeMode(value); // Also update ThemeContext
        break;
      case 'language':
        setLanguage(value);
        break;
      case 'timezone':
        setTimezone(value);
        break;
      case 'emailNotifications':
        setEmailNotifications(value);
        break;
      case 'smsNotifications':
        setSmsNotifications(value);
        break;
    }

    // CRITICAL FIX: Read current values from user.preferences to avoid stale closure values
    // Build the new preferences object using current values from the user object
    // IMPORTANT: API uses snake_case (email_notifications, sms_notifications)
    const currentPrefs = user.preferences;
    const newPreferences = {
      language: key === 'language' ? value : (currentPrefs.language || 'en'),
      timezone: key === 'timezone' ? value : (currentPrefs.timezone || 'UTC'),
      theme: key === 'theme' ? value : (currentPrefs.theme || 'auto'),
      emailNotifications: key === 'emailNotifications' ? value : (currentPrefs.email_notifications ?? true),
      smsNotifications: key === 'smsNotifications' ? value : (currentPrefs.sms_notifications ?? false)
    };

    // Update AuthContext with new preferences (triggers autosave)
    updateUserProfile({
      preferences: newPreferences
    });
  }, [user, updateUserProfile, setThemeMode]);

  // Handle password change
  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate password strength first
    if (!isPasswordValid) {
      setMessage({ text: 'Please ensure your new password meets all the requirements', severity: 'error' });
      setShowSnackbar(true);
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage({ text: 'New passwords do not match', severity: 'error' });
      setShowSnackbar(true);
      return;
    }

    setPasswordLoading(true);

    try {
      await authService.changePassword(currentPassword, newPassword);

      // Reset password fields
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      setMessage({ text: 'Password changed successfully', severity: 'success' });
      setShowSnackbar(true);
    } catch (error: any) {
      // Extract detailed error message from API response
      let errorMessage = 'Failed to change password';

      if (error.response?.data?.error?.message) {
        // API returned a structured error
        errorMessage = error.response.data.error.message;
      } else if (error.response?.data?.message) {
        // Alternative error format
        errorMessage = error.response.data.message;
      } else if (error.message) {
        // Generic error message
        errorMessage = error.message;
      }

      setMessage({ text: errorMessage, severity: 'error' });
      setShowSnackbar(true);
    } finally {
      setPasswordLoading(false);
    }
  };
  
  // Send email verification
  const handleSendVerification = async () => {
    setLoading(true);
    setVerificationEmailMessage(null);

    try {
      await authService.sendVerificationEmail();

      setVerificationEmailMessage({ text: 'Verification email sent successfully', type: 'success' });

      // Clear message after 5 seconds
      setTimeout(() => {
        setVerificationEmailMessage(null);
      }, 5000);
    } catch (error: any) {
      setVerificationEmailMessage({ text: error.message || 'Failed to send verification email', type: 'error' });

      // Clear error message after 5 seconds
      setTimeout(() => {
        setVerificationEmailMessage(null);
      }, 5000);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle 2FA setup
  const handleSetupTwoFactor = async () => {
    setLoading(true);

    try {
      // For TOTP, generate the setup data (QR code, secret, backup codes) FIRST
      if (twoFactorMethod === 'totp') {
        const data = await authService.setupTwoFactor();
        setSetupData(data);
      }
      // For OTP, reset sent state
      if (twoFactorMethod === 'otp') {
        setOtpSent(false);
        setOtpExpiresAt(null);
      }
      // THEN open the dialog
      setShowTwoFactorDialog(true);
    } catch (error: any) {
      setMessage({ text: error.message || 'Failed to set up two-factor authentication', severity: 'error' });
      setShowSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  // Handle sending OTP code
  const handleSendOtpCode = async () => {
    setLoading(true);

    try {
      const result = await authService.sendTwoFactorOtp(otpChannel);
      setOtpSent(true);
      setOtpExpiresAt(result.expiresAt || null);
      setMessage({ text: result.message || `Verification code sent to your ${otpChannel}`, severity: 'success' });
      setShowSnackbar(true);
    } catch (error: any) {
      setMessage({ text: error.message || `Failed to send verification code to ${otpChannel}`, severity: 'error' });
      setShowSnackbar(true);
    } finally {
      setLoading(false);
    }
  };
  
  // Enable 2FA with verification code
  const handleEnableTwoFactor = async () => {
    if (!verificationCode) {
      setMessage({ text: 'Please enter the verification code', severity: 'error' });
      setShowSnackbar(true);
      return;
    }

    setLoading(true);

    try {
      if (twoFactorMethod === 'totp') {
        // TOTP verification (existing flow)
        const response = await authService.enableTwoFactor(verificationCode);
        if (response.user) {
          updateUser(response.user);
        }

        setShowTwoFactorDialog(false);
        setShowBackupCodesDialog(true);
        setTwoFactorEnabled(true);
      } else {
        // OTP verification
        const result = await authService.verifyTwoFactorOtp(verificationCode, otpChannel);

        if (result.valid) {
          setMessage({ text: 'Two-factor authentication enabled successfully', severity: 'success' });
          setShowSnackbar(true);
          setShowTwoFactorDialog(false);
          setTwoFactorEnabled(true);

          // Refresh user data
          await refreshUser();
        } else {
          setMessage({ text: result.message || 'Invalid verification code', severity: 'error' });
          setShowSnackbar(true);
        }
      }
    } catch (error: any) {
      setMessage({ text: error.message || 'Failed to verify code', severity: 'error' });
      setShowSnackbar(true);
    } finally {
      setLoading(false);
    }
  };
  
  // Disable 2FA
  const handleDisableTwoFactor = async () => {
    if (!verificationCode) {
      setMessage({ text: 'Please enter the verification code', severity: 'error' });
      setShowSnackbar(true);
      return;
    }

    setLoading(true);

    try {
      // Call disableTwoFactor with appropriate parameters based on method
      const response = await authService.disableTwoFactor(
        undefined, // password (not used)
        twoFactorMethod === 'otp' ? verificationCode : undefined, // OTP code
        twoFactorMethod === 'totp' ? verificationCode : undefined // TOTP token
      );

      if (response.user) {
        updateUser(response.user);
      }

      setTwoFactorEnabled(false);
      setShowTwoFactorDialog(false);
      setShowDisableTwoFactorDialog(false);
      setVerificationCode('');
      setOtpSent(false);
      setMessage({ text: 'Two-factor authentication disabled', severity: 'success' });
      setShowSnackbar(true);
    } catch (error: any) {
      setMessage({ text: error.message || 'Failed to disable two-factor authentication', severity: 'error' });
      setShowSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  // Simple enable 2FA (no TOTP required - just use verified email/SMS)
  const handleEnableTwoFactorSimple = async () => {
    if (!user?.isEmailVerified) {
      setMessage({ text: 'Please verify your email first', severity: 'error' });
      setShowSnackbar(true);
      return;
    }

    setLoading(true);

    try {
      // Call a new endpoint that enables 2FA without requiring TOTP setup
      const response = await authService.enableTwoFactorSimple();
      if (response.user) {
        updateUser(response.user);
      }

      setTwoFactorEnabled(true);
      setMessage({ text: 'Two-factor authentication enabled', severity: 'success' });
      setShowSnackbar(true);
    } catch (error: any) {
      setMessage({ text: error.message || 'Failed to enable two-factor authentication', severity: 'error' });
      setShowSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  // Setup TOTP authenticator (separate from enabling 2FA)
  const handleSetupTotp = async () => {
    setLoading(true);

    try {
      const data = await authService.setupTwoFactor();
      setSetupData(data);
      setShowTotpSetupDialog(true);
    } catch (error: any) {
      setMessage({ text: error.message || 'Failed to set up authenticator', severity: 'error' });
      setShowSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  // Enable TOTP after verification
  const handleEnableTotp = async () => {
    if (!verificationCode) {
      setMessage({ text: 'Please enter the verification code', severity: 'error' });
      setShowSnackbar(true);
      return;
    }

    setLoading(true);

    try {
      const response = await authService.enableTwoFactor(verificationCode);
      if (response.user) {
        updateUser(response.user);
      }

      setShowTotpSetupDialog(false);
      setShowBackupCodesDialog(true);
      setVerificationCode('');
      setMessage({ text: 'Authenticator app configured successfully', severity: 'success' });
      setShowSnackbar(true);
    } catch (error: any) {
      setMessage({ text: error.message || 'Failed to verify code', severity: 'error' });
      setShowSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  // Disable TOTP (authenticator app) while keeping other 2FA methods
  const handleDisableTotp = async () => {
    if (!verificationCode) {
      setMessage({ text: 'Please provide verification to disable TOTP', severity: 'error' });
      setShowSnackbar(true);
      return;
    }

    setLoading(true);

    try {
      // Depending on verification method selected, pass the appropriate parameter
      const response = await authService.disableTotp(
        undefined, // password - not currently collected in this dialog
        twoFactorMethod === 'otp' ? verificationCode : undefined, // otp_code
        twoFactorMethod === 'totp' ? verificationCode : undefined  // totp_token
      );

      if (response.user) {
        updateUser(response.user);
      }

      setShowDisableTotpDialog(false);
      setVerificationCode('');
      setOtpSent(false);
      setMessage({ text: response.message || 'TOTP has been disabled successfully', severity: 'success' });
      setShowSnackbar(true);
    } catch (error: any) {
      setMessage({ text: error.message || 'Failed to disable TOTP', severity: 'error' });
      setShowSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  // Handle set initial password (for OAuth-only users)
  const handleSetInitialPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isInitialPasswordValid) {
      setMessage({ text: 'Password does not meet requirements', severity: 'error' });
      setShowSnackbar(true);
      return;
    }

    if (initialPassword !== confirmInitialPassword) {
      setMessage({ text: 'Passwords do not match', severity: 'error' });
      setShowSnackbar(true);
      return;
    }

    setInitialPasswordLoading(true);

    try {
      const response = await authService.setInitialPassword(initialPassword);

      // Reload auth methods to reflect the change
      const methods = await authService.getAuthMethods();
      setAuthMethods(methods);

      setInitialPassword('');
      setConfirmInitialPassword('');
      setSetPasswordDialogOpen(false);

      setMessage({ text: response.message || 'Password set successfully. You can now log in with email and password.', severity: 'success' });
      setShowSnackbar(true);
    } catch (error: any) {
      setMessage({ text: error.message || 'Failed to set password', severity: 'error' });
      setShowSnackbar(true);
    } finally {
      setInitialPasswordLoading(false);
    }
  };

  // Handle OAuth provider linking
  const handleLinkOAuthProvider = async (provider: 'google' | 'microsoft') => {
    try {
      // Store the current URL to redirect back after OAuth
      sessionStorage.setItem('redirectAfterOAuth', window.location.pathname);

      // Get the authorization URL from the API
      const { authUrl } = await authService.getOAuthUrl(provider);

      // Redirect to the authorization URL
      window.location.href = authUrl;
    } catch (error: any) {
      setMessage({ text: `Failed to initiate ${provider} OAuth flow`, severity: 'error' });
      setShowSnackbar(true);
    }
  };

  // Handle OAuth provider unlinking
  const handleUnlinkOAuthProvider = async (provider: string) => {
    if (authMethods.totalMethods <= 1) {
      setMessage({
        text: 'Cannot unlink your only authentication method. Please set a password or link another provider first.',
        severity: 'error'
      });
      setShowSnackbar(true);
      return;
    }

    setLoading(true);

    try {
      const response = await authService.unlinkOAuthProvider(provider as any);

      // Reload auth methods to reflect the change
      const methods = await authService.getAuthMethods();
      setAuthMethods(methods);

      setMessage({ text: response.message || `${provider} has been unlinked from your account`, severity: 'success' });
      setShowSnackbar(true);
    } catch (error: any) {
      setMessage({ text: error.message || `Failed to unlink ${provider}`, severity: 'error' });
      setShowSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  // Handle validation of account deletion
  const handleValidateAccountDeletion = async () => {
    setValidationLoading(true);

    try {
      const validation = await userService.validateUserDeletion();
      setValidationResult(validation);
      setShowValidationDialog(true);
    } catch (error: any) {
      setMessage({
        text: error.message || 'Failed to validate account deletion. Please try again.',
        severity: 'error'
      });
      setShowSnackbar(true);
    } finally {
      setValidationLoading(false);
    }
  };

  // Handle account deletion
  const handleDeleteAccount = async () => {
    setValidationLoading(true);

    // Always sign the user out and redirect after deletion. logout() clears
    // tokens/auth state and navigates to /login, so a deleted user is never
    // left stranded on their (now non-existent) profile page.
    const finishDeletion = (text: string, severity: 'success' | 'info' = 'success') => {
      setShowValidationDialog(false);
      setValidationResult(null);
      setMessage({ text, severity });
      setShowSnackbar(true);
      setTimeout(() => {
        logout();
      }, 1500);
    };

    try {
      const result = await userService.deleteAccount();
      console.log('Account deletion result:', result);

      // The delete endpoint only guarantees { success }. deletedEntities is
      // optional — never dereference it unconditionally. Doing so previously
      // threw a TypeError that skipped logout, leaving the deleted account
      // signed in with a misleading error on screen.
      const deleted = (result as any)?.deletedEntities;
      const detail = deleted
        ? ` Removed from ${deleted.organizationMemberships ?? 0} organization(s) and ${deleted.teamMemberships ?? 0} team(s).`
        : '';
      finishDeletion(`Account deleted successfully.${detail}`, 'success');

    } catch (error: any) {
      // If the account is already gone (e.g. retrying after a partial UI
      // failure), the desired end state is already met — sign out cleanly
      // instead of stranding the user with a "user not found" error.
      const httpStatus = error?.status ?? error?.response?.status;
      const alreadyDeleted =
        httpStatus === 404 ||
        error?.code === 'RESOURCE_NOT_FOUND' ||
        /not found|does ?n['’]?t exist/i.test(error?.message || '');

      if (alreadyDeleted) {
        finishDeletion('Your account has already been deleted. Signing you out…', 'info');
        return;
      }

      setMessage({ text: error.message || 'Failed to delete account', severity: 'error' });
      setShowSnackbar(true);
    } finally {
      setValidationLoading(false);
    }
  };

  // Handle content affirmation
  const handleAffirmContent = async () => {
    setAffirmationLoading(true);

    try {
      const currentTermsVersion = '2025-01-17'; // TODO: Get from config
      await userService.affirmContentResponsibility(currentTermsVersion);

      // Reload status
      const status = await userService.getContentAffirmationStatus();
      setAffirmationStatus(status);

      setMessage({ text: 'Content upload responsibility affirmed', severity: 'success' });
      setShowSnackbar(true);
    } catch (error: any) {
      setMessage({ text: error.message || 'Failed to affirm content responsibility', severity: 'error' });
      setShowSnackbar(true);
    } finally {
      setAffirmationLoading(false);
    }
  };

  // Handle content affirmation rescind
  const handleRescindAffirmation = async () => {
    setAffirmationLoading(true);

    try {
      await userService.rescindContentAffirmation();

      // Reload status
      const status = await userService.getContentAffirmationStatus();
      setAffirmationStatus(status);

      setMessage({ text: 'Content affirmation rescinded', severity: 'success' });
      setShowSnackbar(true);
    } catch (error: any) {
      setMessage({ text: error.message || 'Failed to rescind affirmation', severity: 'error' });
      setShowSnackbar(true);
    } finally {
      setAffirmationLoading(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h1"
          sx={{ fontSize: 32, fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.15, marginBottom: 1 }}
        >
          User Profile
        </Typography>
        <Typography sx={ledeSx}>
          Manage your account information, security, and notification preferences.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Basic Profile Information */}
        <Grid xs={12} md={6}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon /> Basic Information
            </Typography>
            
            <Box>
              <TextField
                label="Name"
                variant="outlined"
                fullWidth
                margin="normal"
                value={user?.name || ''}
                onChange={(e) => updateUserProfile({ name: e.target.value })}
                required
              />
              
              <Box>
                <TextField
                  label="Email"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={email}
                  disabled
                  helperText="Email cannot be changed directly"
                />
                {!user?.isEmailVerified && (
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      color="primary"
                      startIcon={<EmailIcon />}
                      onClick={handleSendVerification}
                      disabled={loading}
                    >
                      Verify Email
                    </Button>
                    {verificationEmailMessage && (
                      <Typography variant="caption" color={verificationEmailMessage.type === 'success' ? 'success.main' : 'error.main'}>
                        {verificationEmailMessage.text}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>

              <TextField
                label="Bio"
                variant="outlined"
                fullWidth
                margin="normal"
                multiline
                rows={3}
                value={user?.bio || ''}
                onChange={(e) => updateUserProfile({ bio: e.target.value })}
                placeholder="Tell us a little about yourself..."
              />

              <Box>
                <TextField
                  label="Mobile Phone Number"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={formattedPhoneNumber}
                  onChange={handlePhoneChange}
                  placeholder="1 (555) 123-4567"
                  error={!phoneValidation.isValid}
                  helperText={
                    !phoneValidation.isValid
                      ? phoneValidation.error
                      : phoneValidation.country
                      ? `Valid ${phoneValidation.country} phone number`
                      : 'Start with country code (e.g., 1 for US/Canada). The + is added automatically.'
                  }
                  InputProps={{
                    startAdornment: <PhoneIcon sx={{ mr: 1, color: 'action.disabled' }} />,
                    endAdornment: formattedPhoneNumber && (
                      phoneValidation.isValid ? (
                        <CheckCircleIcon sx={{ color: 'success.main' }} />
                      ) : (
                        <CancelIcon sx={{ color: 'error.main' }} />
                      )
                    )
                  }}
                  inputProps={{
                    type: 'tel',  // Use tel input type for mobile keyboard optimization
                    autoComplete: 'tel'
                  }}
                />
                {formattedPhoneNumber && (
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip title="SMS verification coming soon">
                      <span>
                        <Button
                          variant="outlined"
                          size="small"
                          color="primary"
                          startIcon={<PhoneIcon />}
                          disabled
                        >
                          Verify SMS
                        </Button>
                      </span>
                    </Tooltip>
                    <Typography variant="caption" color="text.secondary">
                      Coming soon
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* User Preferences */}
        <Grid xs={12} md={6}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <SettingsIcon /> Preferences
            </Typography>
            
            <Grid container spacing={2}>
              <Grid xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Language</InputLabel>
                  <Select
                    value={language}
                    label="Language"
                    onChange={(e) => handlePreferenceChange('language', e.target.value)}
                  >
                    <MenuItem value="en">English</MenuItem>
                    <MenuItem value="es">Spanish</MenuItem>
                    <MenuItem value="fr">French</MenuItem>
                    <MenuItem value="de">German</MenuItem>
                    <MenuItem value="ja">Japanese</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Timezone</InputLabel>
                  <Select
                    value={timezone}
                    label="Timezone"
                    onChange={(e) => handlePreferenceChange('timezone', e.target.value)}
                  >
                    <MenuItem value="UTC">UTC</MenuItem>
                    <MenuItem value="America/New_York">Eastern Time</MenuItem>
                    <MenuItem value="America/Chicago">Central Time</MenuItem>
                    <MenuItem value="America/Denver">Mountain Time</MenuItem>
                    <MenuItem value="America/Los_Angeles">Pacific Time</MenuItem>
                    <MenuItem value="Europe/London">London</MenuItem>
                    <MenuItem value="Europe/Paris">Paris</MenuItem>
                    <MenuItem value="Asia/Tokyo">Tokyo</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid xs={12}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Theme</InputLabel>
                  <Select
                    value={theme}
                    label="Theme"
                    onChange={(e) => handlePreferenceChange('theme', e.target.value)}
                  >
                    <MenuItem value="auto">Auto (System Default)</MenuItem>
                    <MenuItem value="light">Light</MenuItem>
                    <MenuItem value="dark">Dark</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                  Notifications
                </Typography>
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={emailNotifications}
                      onChange={(e) => handlePreferenceChange('emailNotifications', e.target.checked)}
                    />
                  }
                  label="Email Notifications"
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={smsNotifications}
                      onChange={(e) => handlePreferenceChange('smsNotifications', e.target.checked)}
                    />
                  }
                  label="SMS Notifications"
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        
        {/* Account Status */}
        <Grid xs={12}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Account Status
            </Typography>
            
            <Grid container spacing={2}>
              <Grid xs={12} sm={6} md={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Email Status
                  </Typography>
                  <Chip 
                    label={user?.isEmailVerified ? 'Verified' : 'Unverified'} 
                    color={user?.isEmailVerified ? 'success' : 'warning'}
                    size="small"
                  />
                </Box>
              </Grid>
              
              <Grid xs={12} sm={6} md={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Mobile Phone
                  </Typography>
                  <Chip
                    label={user?.phoneNumber ? 'Unverified' : 'Not Set'}
                    color={user?.phoneNumber ? 'warning' : 'default'}
                    size="small"
                  />
                </Box>
              </Grid>

              <Grid xs={12} sm={6} md={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Two-Factor Auth
                  </Typography>
                  <Chip
                    label={user?.twoFactorEnabled ? 'Enabled' : 'Disabled'}
                    color={user?.twoFactorEnabled ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
              </Grid>

              <Grid xs={12} sm={6} md={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Account Status
                  </Typography>
                  <Chip
                    label={user?.isActive ? 'Active' : 'Inactive'}
                    color={user?.isActive ? 'success' : 'error'}
                    size="small"
                  />
                </Box>
              </Grid>
              
              <Grid xs={12} sm={6} md={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Last Login
                  </Typography>
                  <Typography variant="body2">
                    {user?.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : 'Never'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        
        {/* Password Change - Only show if user has a password set */}
        {authMethods.hasPassword && (
          <Grid xs={12} md={6}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Change Password
              </Typography>

              <form onSubmit={handlePasswordChange}>
                <TextField
                  label="Current Password"
                  type="password"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                />

                <TextField
                  label="New Password"
                  type="password"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />

                {/* Password strength indicator */}
                {newPassword && (
                  <Box sx={{ mt: 1, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Password Strength
                      </Typography>
                      <Typography variant="body2" color={getPasswordStrengthColor()}>
                        {passwordStrength <= 2 ? 'Weak' : passwordStrength <= 4 ? 'Medium' : 'Strong'}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(passwordStrength / 5) * 100}
                      color={getPasswordStrengthColor()}
                      sx={{ height: 8, borderRadius: 4 }}
                    />

                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Password must have:
                      </Typography>
                      <Grid container spacing={1}>
                        {passwordRequirements.map((req, index) => (
                          <Grid xs={12} sm={6} key={index}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              {req.valid ? (
                                <CheckCircleIcon fontSize="small" color="success" sx={{ mr: 1 }} />
                              ) : (
                                <CancelIcon fontSize="small" color="error" sx={{ mr: 1 }} />
                              )}
                              <Typography
                                variant="body2"
                                color={req.valid ? 'success.main' : 'text.secondary'}
                              >
                                {req.label}
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </Box>
                )}

                <TextField
                  label="Confirm New Password"
                  type="password"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  error={newPassword !== confirmPassword && confirmPassword !== ''}
                  helperText={newPassword !== confirmPassword && confirmPassword !== '' ? 'Passwords do not match' : ''}
                />

                <Box sx={{ mt: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    startIcon={<KeyIcon />}
                    disabled={passwordLoading}
                  >
                    {passwordLoading ? <CircularProgress size={24} /> : 'Change Password'}
                  </Button>
                </Box>
              </form>
            </Paper>
          </Grid>
        )}
        
        {/* Security Settings */}
        <Grid xs={12}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Security Settings
            </Typography>
            
            <Grid container spacing={2}>
              <Grid xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      Two-Factor Authentication
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Add an extra layer of security to your account by requiring a verification code in addition to your password.
                    </Typography>

                    {/* 2FA Toggle - Simple enable/disable */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={twoFactorEnabled}
                            onChange={(e) => {
                              if (e.target.checked) {
                                // Enable 2FA - just need verified email
                                handleEnableTwoFactorSimple();
                              } else {
                                // Disable 2FA - show disable dialog
                                setShowDisableTwoFactorDialog(true);
                              }
                            }}
                            disabled={loading || !user?.isEmailVerified}
                          />
                        }
                        label={twoFactorEnabled ? 'Enabled' : 'Disabled'}
                      />
                      {!user?.isEmailVerified && (
                        <Typography variant="caption" color="error">
                          Verify your email to enable 2FA
                        </Typography>
                      )}
                    </Box>

                    {/* Available 2FA Methods when enabled */}
                    {twoFactorEnabled && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                        <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                          Available 2FA Methods:
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <EmailIcon fontSize="small" color="success" />
                            <Typography variant="body2">
                              Email code - Enabled
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <SmsIcon fontSize="small" color={user?.phoneNumber ? 'success' : 'disabled'} />
                            <Typography variant="body2" color={user?.phoneNumber ? 'text.primary' : 'text.secondary'}>
                              SMS code - {user?.phoneNumber ? 'Enabled' : 'Add phone number to enable'}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PhoneAndroidIcon
                                fontSize="small"
                                color={user?.verified_2fa_methods?.includes('totp') ? 'success' : 'action'}
                              />
                              <Typography variant="body2">
                                Authenticator App (TOTP) - {user?.verified_2fa_methods?.includes('totp') ? 'Configured' : 'Optional'}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={handleSetupTotp}
                                disabled={loading}
                              >
                                {loading ? <CircularProgress size={20} /> : (user?.verified_2fa_methods?.includes('totp') ? 'Reconfigure' : 'Setup TOTP')}
                              </Button>
                              {user?.verified_2fa_methods?.includes('totp') && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  onClick={() => setShowDisableTotpDialog(true)}
                                  disabled={loading}
                                >
                                  Remove
                                </Button>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Authentication Methods Overview */}
              <Grid xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      Authentication Methods
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Manage how you sign in to your account. You can use password and/or OAuth providers.
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <KeyIcon color={authMethods.hasPassword ? 'success' : 'disabled'} />
                          <Typography variant="body2">
                            Password {authMethods.hasPassword ? '(Enabled)' : '(Not set)'}
                          </Typography>
                        </Box>
                        {!authMethods.hasPassword && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setSetPasswordDialogOpen(true)}
                          >
                            Set Password
                          </Button>
                        )}
                      </Box>

                      {['google', 'microsoft'].map(provider => {
                        const isLinked = authMethods.oauthProviders.includes(provider);
                        const Icon = provider === 'google' ? GoogleIcon : MicrosoftIcon;
                        return (
                          <Box key={provider} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Icon color={isLinked ? 'primary' : 'disabled'} />
                              <Typography variant="body2">
                                {provider.charAt(0).toUpperCase() + provider.slice(1)} {isLinked ? '(Linked)' : '(Not linked)'}
                              </Typography>
                            </Box>
                            {isLinked ? (
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                onClick={() => handleUnlinkOAuthProvider(provider)}
                                disabled={loading || authMethods.totalMethods <= 1}
                              >
                                Unlink
                              </Button>
                            ) : (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleLinkOAuthProvider(provider as 'google' | 'microsoft')}
                              >
                                Link {provider.charAt(0).toUpperCase() + provider.slice(1)}
                              </Button>
                            )}
                          </Box>
                        );
                      })}
                    </Box>

                    {authMethods.totalMethods > 0 && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        You have {authMethods.totalMethods} authentication method{authMethods.totalMethods > 1 ? 's' : ''} enabled.
                        You must keep at least one method to access your account.
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Cookie Preferences - GDPR Compliance */}
        <Grid xs={12}>
          <CookiePreferencesSettings />
        </Grid>

        {/* Content Upload Affirmation */}
        <Grid xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleIcon /> Content Upload Responsibility
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              By affirming, you confirm that any content you upload complies with our Terms of Service,
              respects intellectual property rights, and follows applicable laws and regulations.
            </Typography>

            {affirmationStatus && (
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {affirmationStatus.hasAffirmed && !affirmationStatus.requiresReaffirmation ? (
                        <CheckCircleIcon color="success" />
                      ) : (
                        <CancelIcon color="warning" />
                      )}
                      <Typography variant="subtitle1" fontWeight="bold">
                        Status: {affirmationStatus.hasAffirmed && !affirmationStatus.requiresReaffirmation ? 'Affirmed' : 'Reaffirmation Required'}
                      </Typography>
                    </Box>
                    {affirmationStatus.hasAffirmed && !affirmationStatus.requiresReaffirmation && (
                      <Chip
                        label={`Valid until ${new Date(new Date(affirmationStatus.affirmationDate!).getTime() + 90 * 24 * 60 * 60 * 1000).toLocaleDateString()}`}
                        color="success"
                        size="small"
                      />
                    )}
                  </Box>

                  {affirmationStatus.hasAffirmed && affirmationStatus.affirmationDate && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Last affirmed: {new Date(affirmationStatus.affirmationDate).toLocaleString()}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Terms version: {affirmationStatus.termsVersion}
                      </Typography>
                    </Box>
                  )}

                  {affirmationStatus.requiresReaffirmation && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      {affirmationStatus.reason === 'never_affirmed' && 'You have not yet affirmed content upload responsibility. This is required before uploading content.'}
                      {affirmationStatus.reason === 'expired' && 'Your affirmation has expired (>90 days old). Please reaffirm to continue uploading content.'}
                      {affirmationStatus.reason === 'terms_updated' && 'Our Terms of Service have been updated. Please review and reaffirm to continue uploading content.'}
                    </Alert>
                  )}

                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleAffirmContent}
                      disabled={affirmationLoading}
                      startIcon={affirmationLoading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                    >
                      {affirmationStatus.hasAffirmed ? 'Reaffirm Now' : 'Affirm Responsibility'}
                    </Button>

                    {affirmationStatus.hasAffirmed && (
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={handleRescindAffirmation}
                        disabled={affirmationLoading}
                        startIcon={affirmationLoading ? <CircularProgress size={20} /> : <CancelIcon />}
                      >
                        Rescind Affirmation
                      </Button>
                    )}

                    <Button
                      variant="text"
                      component={Link}
                      to="/terms"
                      sx={{ ml: 'auto' }}
                    >
                      View Terms of Service
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            )}

            <Alert severity="info">
              Your affirmation is valid for 90 days and applies to all content you upload during that period.
              Each uploaded content item will include a record of your affirmation for compliance purposes.
            </Alert>
          </Paper>
        </Grid>

        {/* Privacy Center Link */}
        <Grid xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  Privacy & Data Rights
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Download your data, manage privacy settings, and learn about your rights under data protection laws.
                </Typography>
              </Box>
              <Button
                variant="outlined"
                component={Link}
                to="/privacy-center"
                sx={{ ml: 2, minWidth: 150 }}
              >
                Privacy Center
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Danger Zone */}
        <Grid xs={12}>
          <Paper sx={{ p: 3, bgcolor: 'error.light' }}>
            <Typography variant="h6" gutterBottom sx={{ color: 'error.contrastText' }}>
              Danger Zone
            </Typography>
            
            <Typography variant="body2" sx={{ mb: 2, color: 'error.contrastText' }}>
              Once you delete your account, there is no going back. Please be certain.
            </Typography>
            
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleValidateAccountDeletion}
              disabled={validationLoading}
            >
              {validationLoading ? 'Checking...' : 'Delete Account'}
            </Button>
          </Paper>
        </Grid>
      </Grid>

      {/* User Deletion Validation Dialog */}
      <UserDeletionValidationDialog
        open={showValidationDialog}
        onClose={() => {
          setShowValidationDialog(false);
          setValidationResult(null);
        }}
        validation={validationResult}
        onProceedWithDeletion={handleDeleteAccount}
        loading={validationLoading}
      />

      {/* TOTP Setup Dialog */}
      <Dialog
        open={showTotpSetupDialog}
        onClose={() => {
          setShowTotpSetupDialog(false);
          setVerificationCode('');
          setSetupData({ secret: '', qrCodeUrl: '', backupCodes: [] });
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {user?.verified_2fa_methods?.includes('totp') ? 'Reconfigure' : 'Setup'} Authenticator App (TOTP)
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {user?.verified_2fa_methods?.includes('totp')
              ? 'Scan the new QR code with your authenticator app to replace your existing TOTP configuration. Your old codes will stop working after you verify the new setup.'
              : 'Scan the QR code with your authenticator app (like Google Authenticator, Authy, or Microsoft Authenticator).'}
          </DialogContentText>

          {setupData.secret && setupData.qrCodeUrl && (
            <Box sx={{ textAlign: 'center', my: 2 }}>
              <img
                src={setupData.qrCodeUrl}
                alt="QR Code"
                style={{ maxWidth: '200px' }}
              />
              <Typography variant="body2" sx={{ mt: 1, fontFamily: 'monospace' }}>
                Secret key: {setupData.secret}
              </Typography>
            </Box>
          )}

          <TextField
            label="Verification Code"
            variant="outlined"
            fullWidth
            margin="normal"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && verificationCode && !loading) {
                handleEnableTotp();
              }
            }}
            placeholder="Enter the 6-digit code from your app"
            inputProps={{ maxLength: 6 }}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowTotpSetupDialog(false);
            setVerificationCode('');
            setSetupData({ secret: '', qrCodeUrl: '', backupCodes: [] });
          }}>
            Cancel
          </Button>
          <Button
            onClick={handleEnableTotp}
            color="primary"
            variant="contained"
            disabled={loading || !verificationCode}
          >
            {loading ? <CircularProgress size={24} /> : 'Verify and Enable'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Disable 2FA Confirmation Dialog */}
      <Dialog
        open={showDisableTwoFactorDialog}
        onClose={() => {
          setShowDisableTwoFactorDialog(false);
          setVerificationCode('');
          setOtpSent(false);
          setTwoFactorMethod('totp');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Disable Two-Factor Authentication</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {twoFactorMethod === 'totp'
              ? 'Enter the verification code from your authenticator app to confirm.'
              : `We'll send a verification code to your ${otpChannel === 'email' ? 'email address' : 'phone number'} to confirm.`}
          </DialogContentText>

          <Alert severity="warning" sx={{ mb: 2 }}>
            Disabling 2FA will make your account less secure. We recommend keeping it enabled.
          </Alert>

          {/* Verification Method Selection */}
          <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
            <FormLabel component="legend">Verification Method</FormLabel>
            <RadioGroup
              value={twoFactorMethod === 'totp' ? 'totp' : otpChannel}
              onChange={(e) => {
                const value = e.target.value;
                if (value === 'totp') {
                  setTwoFactorMethod('totp');
                } else {
                  setTwoFactorMethod('otp');
                  setOtpChannel(value as 'email' | 'sms');
                }
                setVerificationCode('');
                setOtpSent(false);
              }}
            >
              {/* Only show TOTP option if user has it verified */}
              {user?.verified_2fa_methods?.includes('totp') && (
                <FormControlLabel
                  value="totp"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PhoneAndroidIcon fontSize="small" />
                      <Typography>Authenticator App</Typography>
                    </Box>
                  }
                />
              )}
              {/* Only show Email option if user has it verified or as fallback */}
              {(user?.verified_2fa_methods?.includes('email') ||
                (user?.twoFactorEnabled && user?.isEmailVerified)) && (
                <FormControlLabel
                  value="email"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmailIcon fontSize="small" />
                      <Typography>Email</Typography>
                    </Box>
                  }
                />
              )}
              {/* Only show SMS option if user has it verified */}
              {user?.verified_2fa_methods?.includes('sms') && (
                <FormControlLabel
                  value="sms"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SmsIcon fontSize="small" />
                      <Typography>SMS</Typography>
                    </Box>
                  }
                />
              )}
            </RadioGroup>
          </FormControl>

          {/* TOTP Flow - Just code input */}
          {twoFactorMethod === 'totp' && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter the 6-digit code from your authenticator app or an 8-character backup code
              </Typography>
              <TextField
                label="Verification Code"
                variant="outlined"
                fullWidth
                margin="normal"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="Enter code"
                inputProps={{ maxLength: 8 }}
              />
            </>
          )}

          {/* OTP Flow - Send code then enter code */}
          {twoFactorMethod === 'otp' && otpSent && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Code sent to your {otpChannel}. Check your inbox/messages.
                {otpExpiresAt && ` (Expires in 10 minutes)`}
              </Alert>
              <TextField
                label="Verification Code"
                variant="outlined"
                fullWidth
                margin="normal"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="Enter 6-digit code"
                inputProps={{ maxLength: 6 }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowDisableTwoFactorDialog(false);
            setVerificationCode('');
            setOtpSent(false);
            setTwoFactorMethod('totp');
          }}>
            Cancel
          </Button>

          {/* Show Send Code button for OTP when code not sent yet */}
          {twoFactorMethod === 'otp' && !otpSent && (
            <Button
              onClick={handleSendOtpCode}
              variant="contained"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : `Send Code to ${otpChannel === 'email' ? 'Email' : 'SMS'}`}
            </Button>
          )}

          {/* Show Resend Code button for OTP when code has been sent */}
          {twoFactorMethod === 'otp' && otpSent && (
            <Button
              onClick={handleSendOtpCode}
              variant="outlined"
              disabled={loading}
            >
              Resend Code
            </Button>
          )}

          {/* Show Disable 2FA button when ready (TOTP always ready, OTP after code sent) */}
          {(twoFactorMethod === 'totp' || (twoFactorMethod === 'otp' && otpSent)) && (
            <Button
              onClick={handleDisableTwoFactor}
              color="error"
              variant="contained"
              disabled={loading || !verificationCode}
            >
              {loading ? <CircularProgress size={24} /> : 'Disable 2FA'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Disable TOTP Dialog */}
      <Dialog
        open={showDisableTotpDialog}
        onClose={() => {
          setShowDisableTotpDialog(false);
          setVerificationCode('');
          setOtpSent(false);
          setTwoFactorMethod('totp');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Remove Authenticator App</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {twoFactorMethod === 'totp'
              ? 'Enter the verification code from your authenticator app to confirm removal.'
              : `We'll send a verification code to your ${otpChannel === 'email' ? 'email address' : 'phone number'} to confirm.`}
          </DialogContentText>

          <Alert severity="info" sx={{ mb: 2 }}>
            Removing TOTP will keep your other 2FA methods active (like email verification).
          </Alert>

          {/* Verification Method Selection */}
          <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
            <FormLabel component="legend">Verification Method</FormLabel>
            <RadioGroup
              value={twoFactorMethod === 'totp' ? 'totp' : otpChannel}
              onChange={(e) => {
                const value = e.target.value;
                if (value === 'totp') {
                  setTwoFactorMethod('totp');
                } else {
                  setTwoFactorMethod('otp');
                  setOtpChannel(value as 'email' | 'sms');
                }
                setVerificationCode('');
                setOtpSent(false);
              }}
            >
              {/* Show TOTP option (verify with current authenticator) */}
              <FormControlLabel
                value="totp"
                control={<Radio />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhoneAndroidIcon fontSize="small" />
                    <Typography>Current Authenticator App</Typography>
                  </Box>
                }
              />
              {/* Only show Email option if user has it verified */}
              {(user?.verified_2fa_methods?.includes('email') ||
                (user?.twoFactorEnabled && user?.isEmailVerified)) && (
                <FormControlLabel
                  value="email"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmailIcon fontSize="small" />
                      <Typography>Email</Typography>
                    </Box>
                  }
                />
              )}
              {/* Only show SMS option if user has it verified */}
              {user?.verified_2fa_methods?.includes('sms') && (
                <FormControlLabel
                  value="sms"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SmsIcon fontSize="small" />
                      <Typography>SMS</Typography>
                    </Box>
                  }
                />
              )}
            </RadioGroup>
          </FormControl>

          {/* TOTP Flow - Just code input */}
          {twoFactorMethod === 'totp' && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter the 6-digit code from your authenticator app
              </Typography>
              <TextField
                label="Verification Code"
                variant="outlined"
                fullWidth
                margin="normal"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && verificationCode && !loading) {
                    handleDisableTotp();
                  }
                }}
                placeholder="Enter code"
                inputProps={{ maxLength: 6 }}
                autoFocus
              />
            </>
          )}

          {/* OTP Flow - Send code then enter code */}
          {twoFactorMethod === 'otp' && otpSent && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Code sent to your {otpChannel}. Check your inbox/messages.
                {otpExpiresAt && ` (Expires in 10 minutes)`}
              </Alert>
              <TextField
                label="Verification Code"
                variant="outlined"
                fullWidth
                margin="normal"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && verificationCode && !loading) {
                    handleDisableTotp();
                  }
                }}
                placeholder="Enter 6-digit code"
                inputProps={{ maxLength: 6 }}
                autoFocus
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowDisableTotpDialog(false);
            setVerificationCode('');
            setOtpSent(false);
            setTwoFactorMethod('totp');
          }}>
            Cancel
          </Button>

          {/* Show Send Code button for OTP when code not sent yet */}
          {twoFactorMethod === 'otp' && !otpSent && (
            <Button
              onClick={handleSendOtpCode}
              variant="contained"
              disabled={loading}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !loading) {
                  handleSendOtpCode();
                }
              }}
            >
              {loading ? <CircularProgress size={24} /> : `Send Code to ${otpChannel === 'email' ? 'Email' : 'SMS'}`}
            </Button>
          )}

          {/* Show Resend Code button for OTP when code has been sent */}
          {twoFactorMethod === 'otp' && otpSent && (
            <Button
              onClick={handleSendOtpCode}
              variant="outlined"
              disabled={loading}
            >
              Resend Code
            </Button>
          )}

          {/* Show Remove TOTP button when ready (TOTP always ready, OTP after code sent) */}
          {(twoFactorMethod === 'totp' || (twoFactorMethod === 'otp' && otpSent)) && (
            <Button
              onClick={handleDisableTotp}
              color="error"
              variant="contained"
              disabled={loading || !verificationCode}
            >
              {loading ? <CircularProgress size={24} /> : 'Remove TOTP'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Backup Codes Dialog */}
      <Dialog
        open={showBackupCodesDialog}
        onClose={() => {
          setShowBackupCodesDialog(false);
          setBackupCodesCopied(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Backup Codes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please save these backup codes in a secure location. You can use them to access your account if you lose your authentication device.
          </DialogContentText>

          <Box sx={{ my: 2, p: 2, bgcolor: 'background.default', borderRadius: 1, position: 'relative' }}>
            <IconButton
              onClick={() => {
                const codesText = setupData.backupCodes.join('\n');
                navigator.clipboard.writeText(codesText);
                setBackupCodesCopied(true);
                setTimeout(() => setBackupCodesCopied(false), 2000);
              }}
              sx={{ position: 'absolute', top: 8, right: 8 }}
              size="small"
            >
              <Tooltip title={backupCodesCopied ? "Copied!" : "Copy all codes"}>
                <ContentCopyIcon fontSize="small" />
              </Tooltip>
            </IconButton>
            <Grid container spacing={1}>
              {setupData.backupCodes.map((code, index) => (
                <Grid xs={6} key={index}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {code}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Alert severity="warning">
            Each code can only be used once. Keep them safe!
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowBackupCodesDialog(false);
            setBackupCodesCopied(false);
          }} color="primary">
            I've Saved These Codes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Set Initial Password Dialog */}
      <Dialog
        open={setPasswordDialogOpen}
        onClose={() => setSetPasswordDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Set Password for Your Account</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You currently use OAuth to sign in. Setting a password will allow you to sign in with either your email/password or OAuth providers.
          </DialogContentText>

          <form onSubmit={handleSetInitialPassword}>
            <TextField
              label="New Password"
              type="password"
              fullWidth
              margin="normal"
              value={initialPassword}
              onChange={(e) => setInitialPassword(e.target.value)}
              required
            />

            {/* Password strength indicator */}
            {initialPassword && (
              <Box sx={{ my: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2">
                    Password Strength
                  </Typography>
                  <Typography variant="body2" color={getInitialPasswordStrengthColor()}>
                    {initialPasswordStrength === 5 ? 'Strong' : initialPasswordStrength >= 3 ? 'Medium' : 'Weak'}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={(initialPasswordStrength / 5) * 100}
                  color={getInitialPasswordStrengthColor()}
                />
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Password must have:
                  </Typography>
                  {initialPasswordRequirements.map((req, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {req.valid ? (
                        <CheckCircleIcon fontSize="small" color="success" />
                      ) : (
                        <CancelIcon fontSize="small" color="error" />
                      )}
                      <Typography variant="caption" color={req.valid ? 'success.main' : 'text.secondary'}>
                        {req.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            <TextField
              label="Confirm New Password"
              type="password"
              fullWidth
              margin="normal"
              value={confirmInitialPassword}
              onChange={(e) => setConfirmInitialPassword(e.target.value)}
              error={initialPassword !== confirmInitialPassword && confirmInitialPassword !== ''}
              helperText={initialPassword !== confirmInitialPassword && confirmInitialPassword !== '' ? 'Passwords do not match' : ''}
              required
            />
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSetPasswordDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSetInitialPassword}
            color="primary"
            variant="contained"
            disabled={initialPasswordLoading || !isInitialPasswordValid || initialPassword !== confirmInitialPassword}
          >
            {initialPasswordLoading ? <CircularProgress size={24} /> : 'Set Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for messages */}
      <Snackbar
        open={showSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={() => setShowSnackbar(false)} severity={message.severity as any} sx={{ width: '100%' }}>
          {message.text}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Profile;
